import { GetServerSideProps } from 'next';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useState } from 'react';
import { FaSearch } from 'react-icons/fa';
import Select from 'react-select';
import styled from 'styled-components';
import Button from 'components/Button';
import Page from 'components/Page';
import Pagination from 'components/Pagination';

import PoolCard from 'components/PoolCard';
import { PoolCategory } from 'components/PoolCategories';
import { useLoader } from 'contexts/Loader';
import CoverImage from 'public/pool/vo.png';
import { api } from 'services/apiClient';
import { Paginate, Pool } from 'templates/pool/types';
import { media } from 'utils/media';
import FilterPoolCard, { PoolFilter } from 'components/FilterPoolCard';

export type StaticState = {
  label: string;
  value: string;
};

interface DefaultProps {
  pools: Paginate<Pool>;
  categories: PoolCategory[];
  states: StaticState[];
  periods: [];
}

export default function PoolView({ categories, states, periods }: DefaultProps) {
  const [pools, setPools] = useState<Pool[]>([]);
  const { setLoader } = useLoader();

  const [filter, setFilter] = useState<PoolFilter>();

  const [totalPools, setTotalPools] = useState(0);
  const [totalPoolPages, setTotalPoolPages] = useState(0);

  const [currentPage, setCurrentPage] = useState(1);

  const getPoolsWithFilter = useCallback(
    async (page: number) => {
      setLoader?.(true);

      const { data } = await api.post<Paginate<Pool>>(
        `/pools/_search/?limit=9&page=${page}`,
        filter
          ? {
              category_id: filter.categoryId,
              state: filter.state,
              period: filter.period,
            }
          : undefined,
      );

      const mappedPools = data.values?.map(pool => ({
        ...pool,
        active: pool.status !== 'finished'
      })) ?? [];

      setPools(mappedPools);
      setTotalPools(data.total ?? 0);
      setCurrentPage(page);
      setTotalPoolPages(data.pages);

      setLoader?.(false);
    },
    [setLoader, filter],
  );

  useEffect(() => {
    getPoolsWithFilter(currentPage);
  }, [currentPage, getPoolsWithFilter]);

  const getPools = useCallback(
    async (page: number) => {
      setLoader?.(true);

      const { data } = await api.get<Paginate<Pool>>(`/pools/all/?limit=9&page=${page}`);

      const mappedPools = data.values?.map(pool => ({
        ...pool,
        active: pool.status !== 'finished'
      })) ?? [];

      setPools(mappedPools);
      setTotalPools(data.total ?? 0);
      setCurrentPage(page);
      setTotalPoolPages(data.pages);

      setLoader?.(false);
    },
    [setLoader],
  );

  useEffect(() => {
    getPools(currentPage);
  }, [currentPage, getPools]);

  const handleNextPage = useCallback(() => {
    const next = currentPage + 1;
    if (totalPools >= next) getPools(next);
  }, [currentPage, getPools, totalPools]);

  const handlePreviewPage = useCallback(() => {
    const preview = currentPage - 1;
    if (totalPools > preview) getPools(preview);
  }, [currentPage, getPools, totalPools]);

  const handleOnFilter = useCallback(
    (filter: PoolFilter) => {
      setFilter(filter);
      getPoolsWithFilter(1);
    },
    [getPoolsWithFilter],
  );


  return (
    <Page
      isPublicPage
      navbar
      title="Vaquinha"
      description="Explore as vaquinhas."
    >
      <Container>

          <SearchCategories>
             <FilterPoolCard onSearch={handleOnFilter} />
          </SearchCategories>
  

        <section>
          <div>
            <div>
              <h1>Faça a diferença na vida de outras famílias</h1>
            </div>
           
            <PoolGrid>
              {pools.map((item, index) => (
                <div key={index}>
                  <Link href={`/${item.slug}`}>
                    <a href={`/${item.slug}`}>
                      <PoolCard data={item} />
                    </a>
                  </Link>
                </div>
              ))}
            </PoolGrid>
            <PaginationContainer>
              <Pagination
                onPress={getPools}
                currentPage={currentPage}
                totalPages={totalPoolPages}
                onNextPage={handleNextPage}
                onPreviewPage={handlePreviewPage}
              />
            </PaginationContainer>
          </div>
        </section>
      </Container>
    </Page>
  );
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  min-height: calc(98vh - (400px));

  .swiper-slide {
    width: auto !important;
    margin: 0 2rem;
    margin-bottom: 10rem;
  }

  & > section {
    & h1 {
      font-family: 'P22 Mackinac Pro';
      font-size: 3.5em;
      color: #174F68;
      font-weight: 500;
      line-height: 58px;
      letter-spacing: 0em;
    }

    @media (max-width: 768px) {
      h1 {
        padding: 25px;
        font-size: 2.8em;
      }
    }
    
    & p {
      font-family: 'Nunito';
      font-size: 1.5em;
      font-weight: 400;
      line-height: 27px;
      letter-spacing: 0em;
    }
    .activeSlider {
      opacity: 1;
      width: 25px;
      border-radius: 100px;
      background-color: #ffffff !important;
    }
    &:first-child {
      position: relative;
      max-width: 130em;
      width: 100%;
      margin: 0 auto;

      display: flex;
      flex-direction: row;
      min-height: calc(863px - 114px);
      margin-top: 60px;

      margin-bottom: -150px;

      & > div {
        display: flex;
        flex: 1;


        & h1 {
          margin-top: 40px;
          text-align: left;
          max-width: 600px;
        }

        &:first-child {
          display: flex;
          flex-direction: column;
          color: #174f68;

          & p {
            text-align: left;
            max-width: 591px;
            margin: 13px 0;
          }
        }

        &:last-child {
          display: flex;
          justify-content: flex-end;
        }
      }
    }
    &:nth-child(2) {
      position: relative;
      display: flex;
      justify-content: center;
      align-items: center;
      position: relative;
    }
    &:nth-child(3) {
      background-color: #FBF9ED;
      & > div {
        position: relative;
        //max-width: 130em;
        width: 100%;
        margin: 0 auto;
        padding: 63px /*4rem*/ 0;
        color: #222222;
        display: flex;
        flex-direction: column;

        & h1 {
          text-align: center;
          max-width: 615px;
        }
        

        & > div {
          &:first-child,
          &:nth-child(2) {
            display: flex;
            justify-content: center;
            align-items: center;
          }
          &:nth-child(3) {
            margin: 64px 0;
            align-self: center;
            min-width: 130em;
            display: flex;
            justify-content: center;
            align-items: center;
          }
        }
      }
    }
  }

  ${media('<=tablet')} {
    & > section {
      &:first-child {
        margin-top: 6rem !important;
        flex-direction: column;

        & button {
          width: auto !important;
          margin-bottom: 1rem !important;
        }

        & > div {
          &:first-child {
            & > div:nth-child(2) {
              margin-top: -3rem;
              h1 {
                font-size: 2.7rem;
                line-height: 35px;
              }
            }
          }
        }
      }
      &:nth-child(3) {
        & h1 {
          font-size: 2.7rem;
          line-height: 35px;
        }
      }
    }
  }

  ${media('<=phone')} {
    & > section {
      &:first-child {
        flex-direction: column;

        & button {
          width: auto !important;
          margin-bottom: 1rem !important;
        }
      }
    }
  }
`;

const SearchCategories = styled.div `
  margin: 4rem;
`


export const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const PoolGrid = styled.section`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-self: center;
  align-items: center;
  justify-content: center;
  margin-top: 64px;
  gap: 30px;
  max-width: 130em;
  margin: 34px auto;

  a,
  a:visited,
  a:active {
    text-decoration: none !important;
  }

  ${media('<=tablet')} {
    grid-template-columns: 1fr 1fr;
  }

  ${media('<=phone')} {
    grid-template-columns: 1fr;
  }
`;
